import React, { forwardRef } from "react";

import { cva, type VariantProps } from "class-variance-authority";
import { ChevronDown } from "lucide-react";
import { cn } from "src/shadcn/lib/utils";

const targetVariants = cva(
  "inline-flex items-center justify-between rounded-md border border-input bg-background transition-colors hover:border-muted-foreground focus-visible:outline-none focus-visible:border-primary focus-visible:ring-3 focus-visible:ring-primary/15 cursor-pointer",
  {
    variants: {
      size: {
        small: "h-7 p-0.5 gap-2",
        medium: "h-8 p-1 gap-2",
        large: "h-10 p-1.5 gap-3",
      },
    },
    defaultVariants: {
      size: "large",
    },
  }
);

export interface TargetProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "size">,
    VariantProps<typeof targetVariants> {
  /** Show hex code text beside the color swatch. */
  showHexValue?: boolean;
  /** The original color prop value. */
  color?: string;
  /** The resolved color value to display. */
  colorValue?: string;
}

const Target = forwardRef<HTMLButtonElement, TargetProps>(
  (
    { size = "large", showHexValue, color, colorValue, className, ...props },
    ref
  ) => (
    <button
      ref={ref}
      type="button"
      data-testid="color-picker-target"
      className={cn(targetVariants({ size }), className)}
      {...props}
    >
      {showHexValue && (
        <span className="text-sm font-medium text-foreground min-w-[4.5rem] ps-1">
          {color}
        </span>
      )}
      <span className="inline-flex items-center gap-2">
        <span
          className="block size-5 rounded-md border border-border"
          style={{ backgroundColor: colorValue }}
        />
        <ChevronDown className="size-4 text-muted-foreground" />
      </span>
    </button>
  )
);

Target.displayName = "ColorPicker.Target";

export { Target };
